"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosOilBlendingStockEventSchema = exports.APosOilBlendingEntityTypeSchema = exports.APosOilBlendingStockEventTypeSchema = exports.APosBottleStockByLocationSchema = exports.APosIngredientStockByLocationSchema = exports.APosBottleStockEntrySchema = exports.APosIngredientStockEntrySchema = void 0;
const zod_1 = require("zod");
/**
 * Per-location stock entries for oil-blending entities.
 *
 * Ingredients (Fragrance, CarrierOil) are weighted in mL — single canonical
 * unit across the system; no per-tenant unit toggle (see design doc).
 *
 * Bottles are counted (each).
 *
 * The map lives directly on each entity row (one DDB row per fragrance /
 * carrier / bottle) — no separate stock table. Per-location updates use
 * DynamoDB nested-map UpdateExpressions so two locations don't race.
 */
const decimal3dp = (val) => Number.isFinite(val) && Math.round(val * 1000) / 1000 === val;
const decimal4dp = (val) => Number.isFinite(val) && Math.round(val * 10000) / 10000 === val;
exports.APosIngredientStockEntrySchema = zod_1.z.object({
    quantity_ml: zod_1.z.number().nonnegative().refine(decimal3dp, 'must be 3dp'),
    // Rolling weighted-average cost per mL — updated on each receipt.
    avg_cost_per_ml: zod_1.z.number().nonnegative().refine(decimal4dp, 'must be 4dp'),
    last_received_at: zod_1.z.string().datetime().optional(),
    last_counted_at: zod_1.z.string().datetime().optional(),
    last_event_at: zod_1.z.string().datetime().optional(),
});
exports.APosBottleStockEntrySchema = zod_1.z.object({
    quantity_count: zod_1.z.number().int().nonnegative(),
    // Rolling weighted-average cost in cents per bottle.
    avg_cost_cents: zod_1.z.number().int().nonnegative(),
    last_received_at: zod_1.z.string().datetime().optional(),
    last_counted_at: zod_1.z.string().datetime().optional(),
    last_event_at: zod_1.z.string().datetime().optional(),
});
// Map keys are locationIds. We can't z.record() with EntityIdSchema as the
// key because DDB serialises map keys as strings — runtime validation is on
// the value shape only. The endpoints validate the key against the tenant's
// known locations.
exports.APosIngredientStockByLocationSchema = zod_1.z.record(zod_1.z.string(), exports.APosIngredientStockEntrySchema);
exports.APosBottleStockByLocationSchema = zod_1.z.record(zod_1.z.string(), exports.APosBottleStockEntrySchema);
/**
 * Receipt / count / adjust event — audit log entry written every time stock
 * changes. Lives in its own table (single-table per event type would be
 * heavier than the value provides).
 */
exports.APosOilBlendingStockEventTypeSchema = zod_1.z.enum([
    'receive', 'count', 'adjust', 'transfer_out', 'transfer_in', 'sale_decrement',
]);
exports.APosOilBlendingEntityTypeSchema = zod_1.z.enum(['fragrance', 'carrier_oil', 'bottle']);
exports.APosOilBlendingStockEventSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    eventId: zod_1.z.string().describe('Format: obse_{ulid12}'),
    entityType: exports.APosOilBlendingEntityTypeSchema,
    entityId: zod_1.z.string(),
    locationId: zod_1.z.string(),
    eventType: exports.APosOilBlendingStockEventTypeSchema,
    // Delta uses the entity's native unit (mL or count). Negative = stock out.
    delta: zod_1.z.number(),
    delta_unit: zod_1.z.enum(['ml', 'count']),
    // For receive/transfer events: total cost paid (cents), used to roll avg cost.
    cost_cents: zod_1.z.number().int().nonnegative().optional(),
    // Free-text reason (write-down, spillage, etc.) — required for adjust.
    reason: zod_1.z.string().max(500).optional(),
    // Optional references
    receipt_id: zod_1.z.string().optional(),
    transfer_id: zod_1.z.string().optional(),
    blend_recipe_id: zod_1.z.string().optional(),
    pos_transaction_id: zod_1.z.string().optional(),
    user_id: zod_1.z.string().optional(),
    occurred_at: zod_1.z.string().datetime(),
});
//# sourceMappingURL=oil-blending-stock.schema.js.map